import React from 'react';
import { useLanguage } from '../i18n/LanguageContext';
import { TrophyIcon, TeamCrownIcon } from '../utils/icons';
import { isColorDark } from '../utils/teamAvatars';
import './TeamScoreboard.css';

export default function TeamScoreboard({ teams, teamScores, currentTeamId, compact = false }) {
  const { t } = useLanguage();

  const leaderboard = teams
    .map(team => ({
      ...team,
      score: teamScores[team.id] || 0
    }))
    .sort((a, b) => b.score - a.score);

  const maxScore = Math.max(...leaderboard.map(t => t.score), 1);

  if (compact) {
    return (
      <div className="team-scoreboard-compact">
        {leaderboard.map((team, index) => {
          const needsWhiteText = team.color && isColorDark(team.color);
          return (
            <div 
              key={team.id} 
              className={`team-score-compact ${currentTeamId === team.id ? 'current' : ''} ${needsWhiteText ? 'dark-bg' : ''}`}
              style={team.color ? { backgroundColor: team.color } : {}}
            >
            <div className="team-avatar-dot">
              <img 
                src={team.avatar} 
                alt={`${team.name} avatar`}
                className="team-avatar-small"
              />
            </div>
            <span className="team-name-compact">{team.name}</span>
            <span className="team-score-value">{team.score}</span>
            {index === 0 && team.score > 0 && <TeamCrownIcon className="leader-icon" />}
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <div className="team-scoreboard">
      <div className="scoreboard-header">
        <TrophyIcon />
        <h3>{t('teamMode.scoreboard', 'Team Scores')}</h3>
      </div>
      
      <div className="scoreboard-list">
        {leaderboard.map((team, index) => {
          const needsWhiteText = team.color && isColorDark(team.color);
          return (
            <div 
              key={team.id} 
              className={`scoreboard-item ${currentTeamId === team.id ? 'current' : ''} ${needsWhiteText ? 'dark-bg' : ''}`}
              style={team.color ? { backgroundColor: team.color } : {}}
            >
            <div className="team-info">
              <div className="team-position">
                {index === 0 && team.score > 0 ? (
                  <TeamCrownIcon className="crown-icon" />
                ) : (
                  <span className="position-number">#{index + 1}</span>
                )}
              </div>
              <div className="team-avatar-bar">
                <img 
                  src={team.avatar} 
                  alt={`${team.name} avatar`}
                  className="team-avatar-medium"
                />
              </div>
              <div className="team-details">
                <span className="team-name">{team.name}</span>
                <div className="score-bar">
                  <div 
                    className="score-fill"
                    style={{ 
                      width: `${(team.score / maxScore) * 100}%`,
                      backgroundColor: '#2E4A7A'
                    }}
                  />
                </div>
              </div>
            </div>
            <div className="team-score">{team.score}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function CurrentTeamIndicator({ team, isActive = true }) {
  const { t } = useLanguage();

  if (!team) return null;

  const needsWhiteText = team.color && isColorDark(team.color);

  return (
    <div 
      className={`current-team-indicator ${isActive ? 'active' : 'waiting'} ${needsWhiteText ? 'dark-bg' : ''}`}
      style={team.color ? { backgroundColor: team.color } : {}}
    >
      <div className="team-avatar-circle">
        <img
          src={team.avatar}
          alt={`${team.name} avatar`}
          className="team-avatar-current"
        />
      </div>
      <div className="team-indicator-info">
        <span className="team-turn-label">
          {isActive ? t('teamMode.currentTurn', 'Current Turn') : t('teamMode.waiting', 'Waiting')}
        </span>
        <span className="team-name-display">{team.name}</span>
      </div>
    </div>
  );
}